import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useEffect, useState } from "react";
import { useGetLlmInfo } from "./api/useGetLlmInfo";
import { useDeleteLlmInfo } from "./api/useDeleteLlmInfo";
import { useSortLlm } from "./api/useReorderLlm";
import { useMakeLlmPublic } from "./api/useSetLlmPublic";
import { useMakeLlmPrivate } from "./api/useSetLlmPrivate";
import CenterLoader from "@/shared/components/loader/CenterLoader";
import {
  Plus,
  Trash2,
  GripVertical,
  Pencil,
  ArrowUpDown,
  Save,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { LlmInfoShort } from "./models/LlmInfoShort";
import { AddEditLlmModal } from "./components/AddEditLlmModal";
import { LlmClientsManagement } from "./components/LlmClientsManagement";
import { AsyncToggle } from "@/shared/components/async-toggle";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableRowProps {
  llm: LlmInfoShort;
  onEdit: (llm: LlmInfoShort) => void;
  onDelete: (name: string) => void;
  isReorderMode: boolean;
  isExpanded: boolean;
  onToggleExpand: (llmName: string) => void;
  enableFunction: {
    isDone: boolean;
    call: (name: string) => Promise<void>;
    isFailed: boolean;
  };
  disableFunction: {
    isDone: boolean;
    call: (name: string) => Promise<void>;
    isFailed: boolean;
  };
}

function SortableRow({
  llm,
  onEdit,
  onDelete,
  isReorderMode,
  isExpanded,
  onToggleExpand,
  enableFunction,
  disableFunction,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: llm.name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        {...attributes}
        className={`cursor-pointer hover:bg-gray-50 ${isExpanded ? "bg-gray-50" : ""}`}
        onClick={() => !isReorderMode && onToggleExpand(llm.name)}
      >
        <TableCell>
          <div className="flex items-center gap-2">
            {!isReorderMode && (
              <div className="flex items-center">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </div>
            )}
            {isReorderMode && (
              <div {...listeners} className="cursor-grab hover:cursor-grabbing">
                <GripVertical className="h-4 w-4 text-gray-400" />
              </div>
            )}
            <span className="font-medium">{llm.displayName}</span>
          </div>
        </TableCell>
        <TableCell className="text-sm text-gray-600">{llm.name}</TableCell>
        <TableCell className="text-sm text-gray-500 max-w-xs truncate">
          {llm.description || "-"}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <AsyncToggle
            initialState={llm.isPublic}
            enableFunction={{
              isDone: enableFunction.isDone,
              call: () => enableFunction.call(llm.name),
              isFailed: enableFunction.isFailed,
            }}
            disableFunction={{
              isDone: disableFunction.isDone,
              call: () => disableFunction.call(llm.name),
              isFailed: disableFunction.isFailed,
            }}
            disabled={isReorderMode}
          />
        </TableCell>
        <TableCell>
          {!isReorderMode && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(llm);
                }}
                className="text-gray-600 hover:text-gray-700"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete LLM</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{llm.displayName}"? This
                      action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(llm.name)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={5} className="p-0">
            <LlmClientsManagement
              llmName={llm.name}
              llmDisplayName={llm.displayName}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function AdminLLMManagement() {
  const { llmInfoList, getLlmInfo, isLoading, isFailed } = useGetLlmInfo();
  const { deleteLlmInfo } = useDeleteLlmInfo();
  const { sortLlm } = useSortLlm();
  const {
    makeLlmPublic,
    isMade: isPublicMade,
    isFailed: isPublicFailed,
  } = useMakeLlmPublic();
  const {
    makeLlmPrivate,
    isMade: isPrivateMade,
    isFailed: isPrivateFailed,
  } = useMakeLlmPrivate();
  const [sortedLlmList, setSortedLlmList] = useState<LlmInfoShort[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLlmName, setEditingLlmName] = useState<string | undefined>();
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [originalOrder, setOriginalOrder] = useState<LlmInfoShort[]>([]);
  const [expandedLlms, setExpandedLlms] = useState<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    getLlmInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (llmInfoList) {
      setSortedLlmList([...llmInfoList]);
    }
  }, [llmInfoList]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortedLlmList((items) => {
        const oldIndex = items.findIndex((item) => item.name === active.id);
        const newIndex = items.findIndex((item) => item.name === over?.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleEdit = (llm: LlmInfoShort) => {
    setEditingLlmName(llm.name);
    setIsModalOpen(true);
  };

  const handleDelete = async (name: string) => {
    await deleteLlmInfo(name);
    setSortedLlmList((items) => items.filter((item) => item.name !== name));
  };

  const handleAdd = () => {
    setEditingLlmName(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingLlmName(undefined);
  };

  const handleReorderStart = () => {
    setIsReorderMode(true);
    setOriginalOrder([...sortedLlmList]);
    // Close all expanded LLM clients during reorder
    setExpandedLlms(new Set());
  };

  const handleReorderSave = async () => {
    const orderedLlmNames = sortedLlmList.map((item) => item.name);
    await sortLlm(orderedLlmNames);
    setIsReorderMode(false);
    setOriginalOrder([]);
  };

  const handleReorderCancel = () => {
    setSortedLlmList([...originalOrder]);
    setIsReorderMode(false);
    setOriginalOrder([]);
  };

  const handleToggleExpand = (llmName: string) => {
    setExpandedLlms((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(llmName)) {
        newExpanded.delete(llmName);
      } else {
        newExpanded.add(llmName);
      }
      return newExpanded;
    });
  };

  const handleSuccess = () => {
    // Refresh the list after successful create/update
    getLlmInfo();
  };

  if (isLoading) {
    return <CenterLoader />;
  }

  if (isFailed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>LLM Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32 text-red-500">
            Failed to load LLM information
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>LLM Management</CardTitle>
          <div className="flex items-center gap-2">
            {isReorderMode ? (
              <>
                <Button variant="outline" onClick={handleReorderCancel}>
                  Cancel
                </Button>
                <Button
                  onClick={handleReorderSave}
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Order
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleReorderStart}
                  className="flex items-center gap-2"
                >
                  <ArrowUpDown className="h-4 w-4" />
                  Reorder
                </Button>
                <Button onClick={handleAdd} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add LLM
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {sortedLlmList && sortedLlmList.length > 0 ? (
          <DndContext
            sensors={isReorderMode ? sensors : []}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Public</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <SortableContext
                  items={sortedLlmList.map((llm) => llm.name)}
                  strategy={verticalListSortingStrategy}
                >
                  {sortedLlmList.map((llm) => (
                    <SortableRow
                      key={llm.name}
                      llm={llm}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isReorderMode={isReorderMode}
                      isExpanded={expandedLlms.has(llm.name)}
                      onToggleExpand={handleToggleExpand}
                      enableFunction={{
                        isDone: !!isPublicMade,
                        call: makeLlmPublic,
                        isFailed: !!isPublicFailed,
                      }}
                      disableFunction={{
                        isDone: !!isPrivateMade,
                        call: makeLlmPrivate,
                        isFailed: !!isPrivateFailed,
                      }}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        ) : (
          <div className="flex items-center justify-center h-32 text-gray-500">
            No LLM information available
          </div>
        )}
      </CardContent>

      <AddEditLlmModal
        open={isModalOpen}
        onClose={handleModalClose}
        llmName={editingLlmName}
        onSuccess={handleSuccess}
        availableLlms={sortedLlmList}
      />
    </Card>
  );
}
